// quizService.ts

import api from "@/services/api";
import type { SearchQueryFilters, PaginatedQuizResponse } from "../types/Quiz.dto.types";
import type { QuizBackground, QuizHistoryResponse, QuizUserSummaryResponse, WrongQuestionsResponse } from "@/types/Quiz.types";
import type { QuestionAnswer, QuizPartial, QuizSubmissionResponse } from "@/types/Questions.types";

export interface SubmitQuizResponseDto {
  userId: string;
  totalTimeConsumed: number;
  isCompleted: boolean;
  // attemptNumber: number;
  // quiz: {
  //   id: string;
  //   title: string;
  //   totalQuestions: number;
  // };
  quiz: QuizPartial;
  responses: QuestionAnswer[];
}

export interface SubmitResponse {
  message: string;
  data: QuizSubmissionResponse;
}

// ==================== UPDATED QUIZ API ====================
const quizApi = {
  // Existing APIs
  getQuizzes: async ({ search, limit }: SearchQueryFilters): Promise<PaginatedQuizResponse> => {
    const response = await api.get<PaginatedQuizResponse>(`/quizzes`, {
      params: { search, limit },
    });
    return response.data;
  },

  getQuizByName: async ({ quizName }: { quizName: string }): Promise<QuizBackground> => {
    const response = await api.get<QuizBackground>(`/quizzes/${quizName}`);
    return response.data;
  },

  // ===================== NEW QUIZ RESPONSE APIs =====================

  /** Submit quiz answers (creates a new submission record) */
  submitQuizResponses: async (payload: SubmitQuizResponseDto): Promise<SubmitResponse> => {
    const response = await api.post<SubmitResponse>("/quiz-responses", payload);
    return response.data;
  },

  /** Get the latest submission for a quiz */
  getLatestSubmission: async (quizId: string): Promise<QuizSubmissionResponse> => {
    const response = await api.get<QuizSubmissionResponse>(`/quiz-responses/${quizId}`);
    return response.data;
  },

  /** Get all submissions for a quiz (useful for history tracking charts) */
  getAllSubmissions: async (): Promise<QuizSubmissionResponse[]> => {
    const response = await api.get<QuizSubmissionResponse[]>(`/quiz-responses`, {
      params: { all: true },
    });
    return response.data;
  },

  /** Get a specific submission directly by its absolute submissionId */
  getSubmissionById: async (submissionId: string): Promise<QuizSubmissionResponse> => {
    const response = await api.get<QuizSubmissionResponse>(`/quiz-responses/submission/${submissionId}`);
    return response.data;
  },

  /**
   * Retrieve all uniquely answered wrong questions for the user.
   * This includes questions they got wrong, even if they later got them right.
   */
  getWrongQuestions: async (params?: { page?: number; limit?: number; quizId?: string }): Promise<WrongQuestionsResponse> => {
    const response = await api.get<WrongQuestionsResponse>("/quiz-responses/wrong-questions", {
      params,
    });
    return response.data;
  },

  /**
   * Retrieve questions that the user has NEVER answered correctly.
   * (Note: The URL matches the exact spelling from your backend controller)
   */
  getNeverCorrectQuestions: async (params?: {
    page?: number;
    limit?: number;
    quizId?: string;
  }): Promise<WrongQuestionsResponse> => {
    const response = await api.get<WrongQuestionsResponse>("/quiz-responses/never-corerct-questions", {
      params,
    });
    return response.data;
  },

  getQuizUserSummary: async (): Promise<QuizUserSummaryResponse> => {
    const response = await api.get<QuizUserSummaryResponse>("/quiz-responses/user-summary");
    return response.data;
  },

  getQuizPast30DaysHistory: async (): Promise<QuizHistoryResponse> => {
    const response = await api.get<QuizHistoryResponse>("/quiz-responses/history-30d");
    return response.data;
  },
};

export default quizApi;
